import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EcomEngineAPI from 'api/ecom_engine_api';
import { generateURL } from 'utils/helpers';
import { useVeroTracking } from 'utils/tracking';
import EcomComponentWrapper from 'components/ecom_component_wrapper';
import WalletPasses from 'components/wallet_passes';
import { fetchDocuments } from 'redux/actions/booking';
import { submitToGoogleAnalytics } from 'redux/actions/google_analytics';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { resetCards } from 'redux/slice/gift_card';
import CartSummary from '../cart_summary';
import NewsletterSubscribe from '../newsletter_subscribe';
import RelatedProducts from '../related_products';

export function ConfirmBooking() {
  const booking = useSelector(getCurrentBooking);
  const {
    reference,
    id: bookingId,
    customer_contact_name: contactName,
    customer_contact_email: contactEmail,
  } = booking;
  const documents = useSelector((state) => state.booking.documents);
  const paymentTypeId = useSelector((state) => state.checkout.paymentTypeId);
  const dispatch = useDispatch();
  const { interestedProductsTitle, interestedProductsText } =
    window.ecom.craftConfig.interestedProducts;

  useEffect(() => {
    dispatch(resetCards());
  }, [dispatch]);
  useVeroTracking('web_checkout_complete');

  useEffect(() => {
    // DCS-1413 - CCC-Syd not accepting events for some reason.  Possibly due to
    // the PHP / React integration of GTM.

    // If the checkout is not populated, the user has checked out previously
    // and is likely viewing this confirmation again.  Therefore do not log
    // the event
    if (paymentTypeId !== null) {
      return;
    }
    dispatch(submitToGoogleAnalytics({ payment_type_id: paymentTypeId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, paymentTypeId]);

  const {
    veroSubscribeTitle,
    veroSubscribeText,
    veroActionUrl,
    veroBusinessUnitTag,
    veroSuccessRedirectUrl,
    veroFailureRedirectUrl,
  } = window.ecom.craftConfig.veroSubscribe;

  const [walletPasses, setWalletPasses] = useState([]);

  useEffect(() => {
    dispatch(fetchDocuments(bookingId));

    if (bookingId) {
      EcomEngineAPI.booking
        .walletPasses(bookingId)
        .then(setWalletPasses)
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="confirm-booking space-y-4">
      <div className="h2">Thank you {contactName} for your booking!</div>
      <div className="h5">Your booking reference number is {reference}</div>
      <p>
        An email with your receipt and confirmation has been sent to{' '}
        {contactEmail}.
      </p>
      <EcomComponentWrapper
        className="travel-docs"
        title="Your travel documents"
      >
        <ul className="list-unstyled text-primary space-y-2">
          {documents &&
            documents.map((doc) => (
              <li className="travel-docs__item" key={doc.id}>
                <a
                  className="travel-docs__link"
                  target="_blank"
                  title={doc.name}
                  href={generateURL(
                    `/booking/${bookingId}/documents/${doc.id}?display=true`
                  )}
                  rel="noreferrer"
                >
                  <i className="fas fa-file-pdf me-2" />
                  {doc.name}
                </a>
              </li>
            ))}
        </ul>
      </EcomComponentWrapper>

      <WalletPasses walletPasses={walletPasses} />

      <div className="d-lg-none">
        <CartSummary readOnly paid title="Order Confirmation" />
      </div>

      <RelatedProducts
        title={interestedProductsTitle}
        text={interestedProductsText}
        type="interestedProducts"
        bookingId={bookingId}
      />

      <NewsletterSubscribe
        {...{
          veroSubscribeTitle,
          veroSubscribeText,
          veroActionUrl,
          veroBusinessUnitTag,
          veroSuccessRedirectUrl,
          veroFailureRedirectUrl,
        }}
        className="newsletter-subscribe"
        userFullName={contactName}
        userEmail={contactEmail}
      />
    </div>
  );
}
